// apps/web/src/features/clients/components/ClientIdentifierTakenBlock.tsx
//
// Inline blocking region for the CRM v4 IDENTIFIER_TAKEN 409 (#2242/#2247).
// When the taken identifier resolves to a live client, offers an "open the
// owning client" action; otherwise shows a generic "already in use" message
// with no navigation.
//
// @aiPii NEVER renders a phone, an email or a raw id — the owner is surfaced
//   only as a navigation target.
import { useTranslation } from 'react-i18next';
import type { CSSProperties } from 'react';
import { Button } from '../../../ui/index.js';
import type { IdentifierTakenInfo } from '../../../shared/index.js';

export function ClientIdentifierTakenBlock({
  info,
  onOpenOwner,
  actionStyle,
}: {
  info: IdentifierTakenInfo;
  onOpenOwner: () => void;
  /** Touch sizing from the calling editor; empty on desktop. */
  actionStyle: CSSProperties;
}) {
  const { t } = useTranslation(['clients']);
  return (
    <div
      data-testid="client-identifier-taken"
      style={{
        border: '1px solid var(--status-error-fg)',
        background: 'var(--status-error-bg)',
        borderRadius: 'var(--r-1)',
        padding: 'var(--s-4)',
        marginTop: 'var(--s-4)',
      }}
    >
      <div style={{ fontSize: 'var(--fz-13)', fontWeight: 600, marginBottom: 'var(--s-2)' }}>
        {t('clients:identifierTaken.title')}
      </div>
      <div style={{ fontSize: 'var(--fz-12)', marginBottom: 'var(--s-3)' }}>
        {info.owner ? t('clients:identifierTaken.bodyOwned') : t('clients:identifierTaken.body')}
      </div>
      {info.owner && (
        <Button
          kind="primary"
          style={actionStyle}
          onClick={onOpenOwner}
          data-testid="client-identifier-taken-open"
        >
          {t('clients:identifierTaken.openClient')}
        </Button>
      )}
    </div>
  );
}
